import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const Featured = () => {
  const data = useStaticQuery(graphql`
    {
      featured: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/featured/" } }
        sort: { fields: [frontmatter___date], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              cover {
                childImageSharp {
                  gatsbyImageData(
                    width: 900
                    height: 600
                    placeholder: BLURRED
                    formats: [AUTO, WEBP, AVIF]
                    quality: 95
                    transformOptions: { cropFocus: CENTER, fit: COVER }
                  )
                }
              }
              tech
              github
              external
            }
            html
          }
        }
      }
    }
  `);

  const featuredProjects = data.featured.edges.filter(({ node }) => node);
  const revealTitle = useRef(null);
  const revealProjects = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    sr.reveal(revealTitle.current, srConfig());
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id="projects">
      <h2 className="numbered-heading" ref={revealTitle}>
        Some Things I&apos;ve Built
      </h2>

      {/* projects-grid and featured-project classes defined in global.css */}
      <ul className="projects-grid">
        {featuredProjects &&
          featuredProjects.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { external, title, tech, github, cover } = frontmatter;
            const image = getImage(cover);

            return (
              <li
                key={i}
                className="featured-project"
                ref={el => (revealProjects.current[i] = el)}
                data-project={title}>

                <div className="project-content">
                  <div>
                    <p className="project-overline">Featured Project</p>

                    <h3 className="project-title">
                      <a href={external}>{title}</a>
                    </h3>

                    <div
                      className="project-description"
                      dangerouslySetInnerHTML={{ __html: html }}
                    />

                    {tech.length && (
                      <ul className="project-tech-list">
                        {tech.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>
                    )}

                    <div className="project-links">
                      {(github || external) && (
                        <a
                          href={external || github}
                          aria-label="Project Link"
                          target="_blank"
                          rel="noreferrer"
                          className="project-link-wrapper">
                          <span className="link-text">
                            {external ? 'View Project' : 'View Source Code'}
                          </span>
                          <div className="arrow-icon">
                            <Icon name="Arrow" />
                          </div>
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="project-image" data-project={title}>
                  <a href={external || github || '#'}>
                    <GatsbyImage
                      image={image}
                      alt={title}
                      className="img"
                      loading="lazy"
                      placeholder="blurred"
                    />
                  </a>
                </div>
              </li>
            );
          })}
      </ul>
    </section>
  );
};

export default Featured;
